import { useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store.ts";
import { filteredProjects } from '../../Helpers/Functions.ts';
import { ROLES } from "../../utils/roles.ts";
import roleCheck from "../../utils/roleCheck.ts";
import useAllProjects, { TRefetchFn } from '../../Helpers/CustomHooks/useAllProjects.tsx';
import { AddProjectForm } from '../../components/AddProjectForm/AddProjectForm';
import { setModal } from '../../store/Utilities';
import Pagination from '../../components/Pagination/Pagination.tsx';
import MainTable from '../../components/MainTable/MainTable.tsx';
import usePagination from '../../Helpers/CustomHooks/usePagination.ts';

// UI Elements
import Loader from '../../components/CoreComponents/Loader';
import SearchComponent from '../../components/CoreComponents/Search';
import { IoMdAdd } from "react-icons/io";

export default function ProjectsManagementPage() {
    const { role: userRole, id: userId } = useSelector((state: RootState) => state.SelectedUser.SelectedUser);
    const isAdminUser = roleCheck(userRole, [ROLES.SuperAdmin]);
    const [searchTerm, setSearchTerm] = useState('');
    const dispatch = useDispatch();

    const handleAddEditProject = useCallback((refetch: TRefetchFn, projectNumber?: number) =>
        dispatch(setModal({
            Chilren: () => <AddProjectForm projectNumber={projectNumber} handleRefetchProjects={refetch} />,
            open: true,
            hideModalFooter: true,
        })), [dispatch]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        isLoading,
        handleRefetchProjects
    } = useAllProjects();

    const filteredRows = useMemo(() => filteredProjects(isAdminUser,userId,searchTerm,rows), [rows, searchTerm, isAdminUser, userId]);

    const { currentPage, totalPages, currentRows, handleNextPage, handlePreviousPage, setCurrentPage } =
        usePagination(filteredRows, 25);
        
    const handleSearch = (term: string) => {
        setSearchTerm(term);
        setCurrentPage(1);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleRowClick = useCallback((row: any) => {
        // Only SuperAdmin can edit projects
        if (!isAdminUser) return;
        
        const projectNumber = row.original?.projectNumber;
        if (projectNumber) {
            handleAddEditProject(handleRefetchProjects, projectNumber);
        }
    }, [handleAddEditProject, handleRefetchProjects, isAdminUser]);

    return (
        <>
            {isLoading ? <Loader loaders={{ isProjectsLoading: true }} /> :
                <div className="overflow-x-auto min-h-[600px]">
                    <div className="flex justify-between items-center mb-4">
                        {/* Search Component */}
                        <div className="flex-grow mr-2">
                            <SearchComponent onSearch={handleSearch} />
                        </div>
                        {isAdminUser &&
                            <button
                                type="button"
                                className="bg-orange-1000 hover:bg-gray-800 float-right"
                                title="Add Project"
                                onClick={() => handleAddEditProject(handleRefetchProjects)}>
                                <span className="flex items-center text-sm font-medium text-white">
                                    <IoMdAdd size={18} />
                                    <span className="ml-1">Add Project</span>
                                </span>
                            </button>
                        }
                    </div>
                    <MainTable
                        getTableProps={getTableProps}
                        getTableBodyProps={getTableBodyProps}
                        headerGroups={headerGroups}
                        prepareRow={prepareRow}
                        currentRows={currentRows}
                        rows={rows}
                        onRowClick={isAdminUser ? handleRowClick : undefined}
                    />
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        handleNextPage={handleNextPage}
                        handlePreviousPage={handlePreviousPage}
                    />
                </div>
            }
        </>
    );
}
